import { Component } from '@theme/component';

/** Catalog-backed WhatsApp enquiry; it never creates a paid Shopify order. */
class ChacecaHome extends Component {
  #controller;
  #cart = [];
  #catalog = new Map();
  #texts = {};
  #timer;
  #lastTrigger;
  #storageKey = 'chaceca-whatsapp-order-v1';

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#timer);
    this.querySelector('.cc-cart')?.close();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#texts = JSON.parse(this.querySelector('.cc-translations').textContent);
    this.#catalog = new Map([...this.querySelectorAll('.cc-product')].map(card => [card.dataset.productId, {
      id: card.dataset.productId, name: card.dataset.name, price: Number(card.dataset.price),
      image: card.querySelector('img')?.getAttribute('src') || '', quantity: 1
    }]));
    try {
      const saved = JSON.parse(localStorage.getItem(this.#storageKey) || '[]');
      this.#cart = Array.isArray(saved) ? saved.filter(item => this.#catalog.has(item.id) && Number.isSafeInteger(item.quantity) && item.quantity > 0 && item.quantity <= 99).map(item => ({...this.#catalog.get(item.id), quantity:item.quantity})) : [];
    } catch { this.#cart = []; }
    const signal = this.#controller.signal;
    document.addEventListener('click', event => { if (!this.querySelector('.cc-header').contains(event.target)) this.#hideMenu(); }, {signal});
    document.addEventListener('keydown', event => { if (event.key === 'Escape' && !this.querySelector('.cc-navigation').hidden) { this.#hideMenu(); this.querySelector('.cc-menu-toggle').focus(); } }, {signal});
    this.querySelector('.cc-navigation').addEventListener('click', event => { if (event.target.closest('a')) this.#hideMenu(); }, {signal});
    const drawer = this.querySelector('.cc-cart');
    drawer.addEventListener('close', () => this.#lastTrigger?.focus(), {signal});
    drawer.addEventListener('click', event => { if (event.target === drawer) { const b = drawer.getBoundingClientRect(); if (event.clientX < b.left || event.clientX > b.right || event.clientY < b.top || event.clientY > b.bottom) drawer.close(); } }, {signal});
    this.#render();
  }

  #money(price) { return new Intl.NumberFormat(document.documentElement.lang || 'es', {style:'currency',currency:this.dataset.currency || 'USD'}).format(price / 100); }
  #element(tag, className, text) { const node = document.createElement(tag); node.className = className; if (text !== undefined) node.textContent = text; return node; }
  #notify(text) { const toast = this.querySelector('.cc-toast'); toast.textContent = text; toast.classList.add('is-visible'); clearTimeout(this.#timer); this.#timer = setTimeout(() => toast.classList.remove('is-visible'), 2200); }
  #hideMenu() { this.querySelector('.cc-navigation').hidden = true; this.querySelector('.cc-menu-toggle').setAttribute('aria-expanded','false'); }

  /** @param {MouseEvent} event */
  toggleMenu(event) { const nav = this.querySelector('.cc-navigation'); nav.hidden = !nav.hidden; this.querySelector('.cc-menu-toggle').setAttribute('aria-expanded',String(!nav.hidden)); }
  /** @param {MouseEvent} event */
  openOrder(event) { event.preventDefault(); this.#lastTrigger = event.target.closest('a,button'); this.querySelector('.cc-cart').showModal(); }
  /** @param {MouseEvent} event */
  closeOrder(event) { this.querySelector('.cc-cart').close(); }
  /** @param {MouseEvent} event */
  filterProducts(event) {
    const button = event.target.closest('[data-filter]');
    if (!button) return;
    this.querySelectorAll('[data-filter]').forEach(other => other.setAttribute('aria-pressed',String(other === button)));
    let count = 0;
    this.querySelectorAll('.cc-product').forEach(card => { card.hidden = button.dataset.filter !== 'all' && card.dataset.category !== button.dataset.filter; if (!card.hidden) count++; });
    this.querySelector('.cc-empty-filter').hidden = count > 0;
    this.querySelector('.cc-filter-status').textContent = this.#texts.count.replace('{{ count }}',String(count));
  }
  /** @param {MouseEvent} event */
  addRequest(event) {
    event.preventDefault();
    const card = event.target.closest('.cc-product');
    const product = this.#catalog.get(card?.dataset.productId);
    if (!product) return;
    const existing = this.#cart.find(item => item.id === product.id);
    if (existing?.quantity >= 99) return this.#notify(this.#texts.limit);
    if (existing) existing.quantity++; else this.#cart.push({...product});
    this.#render();
    this.#notify(this.#texts.added);
    this.#lastTrigger = event.target.closest('a,button');
    this.querySelector('.cc-cart').showModal();
  }

  #change(id, quantity) {
    if (quantity > 99) return this.#notify(this.#texts.limit);
    const label = document.activeElement?.getAttribute('aria-label');
    this.#cart = this.#cart.map(item => item.id === id ? {...item,quantity} : item).filter(item => item.quantity > 0);
    this.#render();
    const focus = [...this.querySelectorAll('.cc-cart-items button')].find(button => button.getAttribute('aria-label') === label);
    (focus || this.querySelector('.cc-close')).focus();
    this.#notify(quantity ? this.#texts.updated : this.#texts.removed);
  }
  #quantityButton(text, label, item, change) { const button = this.#element('button','',text); button.type = 'button'; button.setAttribute('aria-label',`${label}: ${item.name}`); button.addEventListener('click',() => this.#change(item.id,item.quantity + change)); return button; }

  #render() {
    const count = this.#cart.reduce((sum,item) => sum + item.quantity,0);
    const subtotal = this.#cart.reduce((sum,item) => sum + item.quantity * item.price,0);
    this.querySelector('.cc-cart-count').textContent = count;
    this.querySelector('.cc-total').textContent = this.#money(subtotal);
    const container = this.querySelector('.cc-cart-items'); container.replaceChildren();
    if (!count) container.append(this.#element('p','cc-cart-empty',this.#texts.empty));
    for (const item of this.#cart) {
      const row = this.#element('div','cc-cart-row');
      if (item.image) { const image = this.#element('img','cc-cart-row-image'); image.src = item.image; image.alt = ''; row.append(image); }
      const copy = this.#element('div','cc-cart-row-copy'); copy.append(this.#element('h3','',item.name),this.#element('p','',this.#money(item.price)));
      const controls = this.#element('div','cc-row-controls');
      const quantities = this.#element('div','cc-quantity'); quantities.append(this.#quantityButton('−',this.#texts.decrease,item,-1),this.#element('span','',item.quantity),this.#quantityButton('+',this.#texts.increase,item,1));
      const remove = this.#element('button','cc-remove',this.#texts.remove); remove.type = 'button'; remove.setAttribute('aria-label',`${this.#texts.remove}: ${item.name}`); remove.addEventListener('click',() => this.#change(item.id,0));
      controls.append(quantities,remove); copy.append(controls); row.append(copy,this.#element('span','cc-cart-row-total',this.#money(item.quantity * item.price))); container.append(row);
    }
    const message = [this.#texts.intro,'',...this.#cart.map(item => `${item.quantity}x ${item.name} (${this.#money(item.price * item.quantity)})`),'',`${this.#texts.subtotal}: ${this.#money(subtotal)}`,'',this.#texts.outro].join('\n');
    const link = this.querySelector('.cc-checkout'); link.href = `https://wa.me/${this.dataset.whatsapp}?text=${encodeURIComponent(message)}`; link.setAttribute('aria-disabled',String(count === 0));
    link.onclick = event => { if (!count) event.preventDefault(); };
    try { localStorage.setItem(this.#storageKey,JSON.stringify(this.#cart.map(({id,quantity}) => ({id,quantity})))); } catch {}
  }
}

if (!customElements.get('chaceca-home-component')) customElements.define('chaceca-home-component',ChacecaHome);
